import { fatal, warnIfDebug, welcome } from 'utils/log';
import { missingPlugin } from 'config/errors';
import { ensureArray, combine } from 'utils/array';
import { findInViewHierarchy } from 'shared/registry';
import dataConfigurator from './config/custom/data';
import RootModel from 'src/model/RootModel';
import hooks from 'src/events/Hook';
import subscribe from './helpers/subscribe';
import blcktive from '../blcktive';
import { ATTRIBUTE, BINDING_FLAG, DECORATOR, INTERPOLATOR, TRANSITION } from 'config/types';
import { assign, create, hasOwn } from 'utils/object';
import { isArray, isString } from 'utils/is';
import { compute } from 'src/blcktive/prototype/compute';
import getblcktiveContext from '../shared/getblcktiveContext';

const registryNames = [
  'adaptors',
  'components',
  'decorators',
  'easing',
  'events',
  'interpolators',
  'partials',
  'transitions'
];

const protoRegistries = ['computed', 'helpers'];

let uid = 0;

export default function construct(blcktive, options) {
  if (blcktive.DEBUG) welcome();

  initialiseProperties(blcktive);
  handleAttributes(blcktive);

  // set up event subscribers
  subscribe(blcktive, options, 'on');

  // if there's not a delegation setting, inherit from parent if it's not default
  if (
    !hasOwn(options, 'delegate') &&
    blcktive.parent &&
    blcktive.parent.delegate !== blcktive.delegate
  ) {
    blcktive.delegate = false;
  }

  // plugins that need to run at construct
  if (isArray(options.use)) {
    blcktive.use.apply(blcktive, options.use.filter(p => p.construct));
  }

  hooks.construct.fire(blcktive, options);
  if (options.onconstruct) options.onconstruct.call(blcktive, getblcktiveContext(blcktive), options);

  // Add registries
  let i = registryNames.length;
  while (i--) {
    const name = registryNames[i];
    blcktive[name] = assign(create(blcktive.constructor[name] || null), options[name]);
  }

  i = protoRegistries.length;
  while (i--) {
    const name = protoRegistries[i];
    blcktive[name] = assign(create(blcktive.constructor.prototype[name]), options[name]);
  }

  if (blcktive._attributePartial) {
    blcktive.partials['extra-attributes'] = blcktive._attributePartial;
    delete blcktive._attributePartial;
  }

  // Create a viewmodel
  const viewmodel = new RootModel({
    adapt: getAdaptors(blcktive, blcktive.adapt, options),
    data: dataConfigurator.init(blcktive.constructor, blcktive, options),
    blcktive
  });

  // once resolved, share the adaptors array between the root model and instance
  blcktive.adapt = viewmodel.adaptors;

  blcktive.viewmodel = viewmodel;

  for (const k in blcktive.computed) {
    compute.call(blcktive, k, blcktive.computed[k]);
  }
}

function getAdaptors(blcktive, protoAdapt, options) {
  protoAdapt = protoAdapt.map(lookup);
  const adapt = ensureArray(options.adapt).map(lookup);

  const srcs = [protoAdapt, adapt];
  if (blcktive.parent && !blcktive.isolated) {
    srcs.push(blcktive.parent.viewmodel.adaptors);
  }

  return combine.apply(null, srcs);

  function lookup(adaptor) {
    if (isString(adaptor)) {
      adaptor = findInViewHierarchy('adaptors', blcktive, adaptor);

      if (!adaptor) {
        fatal(missingPlugin(adaptor, 'adaptor'));
      }
    }

    return adaptor;
  }
}

function initialiseProperties(blcktive) {
  // Generate a unique identifier, for places where you'd use a weak map if it
  // existed
  blcktive._guid = 'r-' + uid++;

  // events
  blcktive._subs = create(null);
  blcktive._nsSubs = 0;

  // storage for item configuration from instantiation to reset,
  // like dynamic functions or original values
  blcktive._config = {};

  // events
  blcktive.event = null;
  blcktive._eventQueue = [];

  // observers
  blcktive._observers = [];

  // external children
  blcktive._children = [];
  blcktive._children.byName = {};
  blcktive.children = blcktive._children;

  if (!blcktive.component) {
    blcktive.root = blcktive;
    blcktive.parent = blcktive.container = null; // TODO container still applicable?
  }
}

function handleAttributes(blcktive) {
  const component = blcktive.component;
  const attributes = blcktive.constructor.attributes;

  if (attributes && component) {
    const tpl = component.template;
    const attrs = tpl.m ? tpl.m.slice() : [];

    // grab all of the passed attribute names
    const props = attrs.filter(a => a.t === ATTRIBUTE).map(a => a.n);

    // warn about missing requireds
    attributes.required.forEach(p => {
      if (!~props.indexOf(p)) {
        warnIfDebug(`Component '${component.name}' requires attribute '${p}' to be provided`);
      }
    });

    // set up a partial containing non-property attributes
    const all = attributes.optional.concat(attributes.required);
    const partial = [];
    let i = attrs.length;
    while (i--) {
      const a = attrs[i];
      if (a.t === ATTRIBUTE && !~all.indexOf(a.n)) {
        if (attributes.mapAll) {
          // map the attribute if requested and make the extra attribute in the partial refer to the mapping
          partial.unshift({
            t: ATTRIBUTE,
            n: a.n,
            f: [{ t: INTERPOLATOR, r: `~/${a.n}` }]
          });
        } else {
          // transfer the attribute to the extra attributes partal
          partial.unshift(attrs.splice(i, 1)[0]);
        }
      } else if (
        !attributes.mapAll &&
        (a.t === DECORATOR || a.t === TRANSITION || a.t === BINDING_FLAG)
      ) {
        partial.unshift(attrs.splice(i, 1)[0]);
      }
    }

    if (partial.length) component.template = { t: tpl.t, e: tpl.e, f: tpl.f, m: attrs, p: tpl.p };
    blcktive._attributePartial = partial;
  }
}
